<template>
  <div class="room-info">
    <SidebarHeader
      class="room-info-header"
      :click="onSidebarBack"
      :menuItems="[
        ...adminMenuItems,
        {
          icon: 'delete',
          show: !isHubOrLounge && roomLifetime === 'fixed' && userLevel > 0,
          text: 'Delete Room',
          style: { color: 'var(--not-active-user)' },
          click: () => deleteRoom(data)
        }
      ]"
    >
      <template #title>
        {{ data.name }}
        <h4 class="room-info-header__space-name" v-if="isDesktop">{{ spaceName }}</h4>
      </template>
    </SidebarHeader>

    <div class="room-info-content" @scroll="onScroll">
      <button v-if="!isUserInRoom && !isHub" class="room-info-join" @click="handleSwitchRoom(data.id)">
        <img class="room-info-join__icon" :src="require(`@/assets/${buttonContent.icon}`)" />
        {{ buttonContent.text }}
      </button>
      <section v-if="isGuest" class="guest-info">
        <header class="guest-info__title">You have been invited to this Room as a guest.</header>
        <div class="guest-info__body" v-if="isExternalGuest">
          SPACEIN is a cloud-based video conferencing and event tool for distributed office teams and communities. Why not
          create your own community?
        </div>
        <a href="https://spacein.co.uk" target="_blank" class="guest-info__cta" v-if="isExternalGuest">
          <span class="guest-info__cta-body"> Find out more </span>
          <img class="guest-info__cta-caret" aria-hidden="true" src="@/assets/global-icons/arrow-left-black.svg" />
        </a>
      </section>
      <div v-if="data.conferenceType === 'spacein' && !isHubOrLounge" class="room-info-block">
        <img src="@/assets/global-icons/clock.svg" />Opened {{ openedDaysAgo >= 6 ? 'on' : 'at' }} {{ openedAtString }}
      </div>
      <div class="room-info-participants">
        <div class="room-info-block">
          <img src="@/assets/global-icons/user-circle.svg" />
          {{ data.location.online }}
          {{ buttonContent.users }}{{ data.location.online !== 1 && data.conferenceType === 'spacein' ? 's' : '' }}
          <img
            src="@/assets/global-icons/refresh.svg"
            @click="refreshParticipants"
            v-if="shouldShowRefreshButton"
            class="room-info-block__refresh"
          />
        </div>
        <SearchResultUser
          v-if="(isUserInRoom && isPasswordProtected) || !isPasswordProtected"
          v-for="user in participants"
          :key="user.id"
          type="room-info"
          :data="user"
          :showStatus="isHub"
          :isSpaceAdmin="data.isSpaceAdmin"
        />
        <template v-if="isLoadingParticipants">
          <Skeleton
            v-for="i in 3"
            :key="i + participants.length"
            size="100% auto"
            padding="12px 12px 12px 63px"
            rounded
            gradient
            noverflow
            shadow-light
          >
            <Skeleton size="45px" circle shimmer absolute offsets="10px auto auto 10px" />
            <Skeleton size="75% 17px" margin="0 0 9px" shimmer rounded-full />
            <Skeleton size="60% 12px" margin="4px 0" shimmer rounded-full />
            <Skeleton size="40% 12px" margin="4px 0" shimmer rounded-full />
          </Skeleton>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import RoomAdminSettingsMixin from '@/components/sidebar/RoomAdminSettingsMixin.js';
import SearchResultUser from '@/components/search/SearchResultUser.vue';
import Skeleton from '@/components/global/loading/Skeleton.vue';
import SidebarHeader from '../SidebarHeader.vue';

import { displayHappenedAtDate, daysSinceNow } from '@/functions/utility.js';

const linkButtonContent = { text: 'Explore', icon: 'global-icons/eye-show-small.svg', users: 'Exploring' };

const buttonContentMap = {
  spacein: {
    text: 'Join room',
    icon: 'notification-icons/camcorder-white.svg',
    users: 'Guest'
  },
  livestream: {
    text: 'Watch',
    icon: 'conferencing-icons/play.svg',
    users: 'Watching'
  },
  link: linkButtonContent,
  internal: linkButtonContent,
  // legacy, deprecated (probably)
  networking: linkButtonContent,
  unknown: linkButtonContent
};

export default {
  components: { SearchResultUser, Skeleton, SidebarHeader },
  mixins: [RoomAdminSettingsMixin],
  computed: {
    ...mapGetters({
      data: 'getCurrentSpacePageData',
      user: 'current_user/data',
      userLevel: 'current_user/currentAreaPermissionNo',
      isAllowed: 'permissions/isAllowed',
      isPremiumEnabled: 'getCurrentAreaPremiumFeaturesEnabled',
      spaceName: 'getSpaceParentName',
      isDesktop: 'viewport/isDesktop'
    }),
    ...mapGetters(['getIfOnJoinedRoomPage', 'getCurrentUsersRoomId']),
    ...mapGetters('search', [
      'ifLoadingParticipants',
      'spaceParticipants',
      'isLoadingMoreParticipants',
      'isThereMoreParticipants'
    ]),
    ...mapGetters({
      isGuest: 'current_user/isGuest',
      isExternalGuest: 'current_user/isExternalGuest'
    }),
    isLoadingInitialParticipants: (vm) => vm.ifLoadingParticipants,
    isLoadingParticipants: (vm) => vm.ifLoadingParticipants || vm.isLoadingMoreParticipants,
    openedAt: (vm) => new Date(vm.data.dateAdded),
    openedAtString: (vm) => displayHappenedAtDate(vm.openedAt),
    openedDaysAgo: (vm) => daysSinceNow(vm.openedAt),
    buttonContent: (vm) => buttonContentMap[vm.data.conferenceType],
    isHub: (vm) => vm.data.location.spaceType === 'hub',
    isHubOrLounge: (vm) => vm.isHub || vm.data.location.spaceType === 'lounge',
    isUserCreator: (vm) => vm.user.email === vm.data.hostEmail,
    isUserInRoom: (vm) => vm.getCurrentUsersRoomId === vm.data.id,
    shouldShowRefreshButton: (vm) => vm.spaceParticipants.length > 20, // backend will stop sending updates at this point, so require manual refresh,
    isPasswordProtected: (vm) => vm.data.passwordEnabled,
    participants: (vm) => {
      let results = vm.spaceParticipants;
      if (!vm.isAllowed('room.lobby.acceptOrDeny')) {
        results = results.filter((r) => !r.inLobby);
      }

      return results;
    }
  },
  methods: {
    ...mapActions([
      'deleteRoom',
      'handleSwitchRoom',
      'stopViewingRoom',
      'modal/open',
      'sidebar/close',
      'routing/goToCustomise',
      'handleToggleRoomLobby',
      'handleRemoveRoomPassword',
      'getUpdatedSpaceData'
    ]),
    ...mapActions('search', ['fetchCurrentSpaceParticipants', 'fetchMoreSpaceParticipants', 'refreshSpaceParticipants']),
    onScroll(e) {
      const el = e.target;
      const scrollY = el.clientHeight + el.scrollTop;
      const distanceFromBottom = el.scrollHeight - scrollY;

      if (distanceFromBottom < 100 && this.isThereMoreParticipants) {
        this.fetchMoreSpaceParticipants();
      }
    },
    refreshParticipants() {
      this.refreshSpaceParticipants();
    },
    onSidebarBack() {
      if (this.isUserInRoom) {
        this['sidebar/close']();
      } else {
        this.stopViewingRoom();
      }
    },
    getUpdatedRoomData() {
      //update room info on mount when in the room (to make sure guest toggles are up to date)
      if (this.isUserInRoom) {
        this.getUpdatedSpaceData(this.data.id);
      }
    }
  },
  mounted() {
    this.fetchCurrentSpaceParticipants();
    this.getUpdatedRoomData();
  },
  destroyed() {
    this.stopViewingRoom();
  }
};
</script>

<style lang="scss" scoped>
.room-info {
  display: flex;
  flex-flow: column;
  box-sizing: border-box;
  height: 100%;

  &-content {
    color: white;
    display: flex;
    flex-flow: column;
    flex-grow: 1;
    gap: 10px;
    font-size: 0.875rem;
    font-weight: bold;
    height: 100%;
    overflow-y: auto;
    box-sizing: border-box;
    padding: 20px;
  }

  &-header {
    box-shadow: none !important;

    + * {
      margin-top: 0 !important;
    }

    &__name {
      font-size: 22px;
      margin-bottom: 35px;
      overflow: hidden;
    }
    &__menu {
      flex-shrink: 0;
    }
    &__space-name {
      margin-top: 6px;
      color: #b3b3b3;
      font-size: 0.9rem;
    }
  }

  &-join {
    border: none;
    text-align: left;
    background-color: #3bc146;
    display: flex;
    align-items: center;
    color: inherit;
    font-size: inherit;
    font-weight: inherit;
    border-radius: 100px;
    cursor: pointer;
    padding: 12px;

    &__icon {
      margin-right: 7px;
      height: 16px;
      opacity: 0.6;
      transition: opacity 0.125s;
    }

    &:hover &__icon {
      opacity: 1;
    }
  }

  &-block {
    display: flex;
    align-items: center;
    padding: 10px;
    border-radius: 100px;
    background-color: #{$grey-7}80;
    line-height: 1.33;

    &__refresh {
      text-decoration: underline;
      margin-left: auto;
      margin-right: 10px;
      display: block;
      cursor: pointer;
    }

    > img {
      opacity: 0.5;
      height: 20px;
      margin-right: 10px;
    }
  }

  &-participants {
    display: flex;
    flex-flow: column;
    gap: 10px;
    margin-top: 20px;
  }
}

.guest-info {
  background-color: #{$grey-7}80;
  padding: 20px;
  border-radius: 17px;
  font-size: 0.875rem; /** 14px */
  color: white;

  &__title {
    font-weight: bold;
    margin-bottom: 20px;
  }

  &__body {
    font-weight: normal;
    color: $grey-2;
    line-height: 1.4;
  }

  &__cta {
    color: $spacein-yellow;
    display: inline-block;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    &-caret {
      height: 12px;
      filter: brightness(0) saturate(100%) invert(75%) sepia(72%) saturate(571%) hue-rotate(359deg) brightness(102%)
        contrast(105%);
      transform: rotate(180deg);
    }
  }
}
</style>
